import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useNotifications } from 'providers/notifications/useNotifications';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAutoCreateMerchantsBankAccountMutation, useTriggerAutoKybMutation } from 'services/merchants';

import { ValidationAPIError } from 'types/error';
import { NotificationSeverity } from 'types/notifications';

import { getValidationErrorMessage } from 'utils/error';

import { LABELS } from './keys';

export const useAutoKybFlow = ({ uuid }: { uuid?: string }) => {
  const { t } = useTranslation();
  const { pushToast } = useNotifications();

  const [triggerAutoKyb] = useTriggerAutoKybMutation();
  const [autoCreateBankAccount] = useAutoCreateMerchantsBankAccountMutation();

  const [isProcessing, setIsProcessing] = useState(false);

  const showApiErrorToast = (error: unknown, fallbackMessage?: string) => {
    const errorData = (error as FetchBaseQueryError)?.data as ValidationAPIError | undefined;
    const errorMessage = getValidationErrorMessage(errorData);
    const message = errorMessage ?? fallbackMessage ?? t(LABELS.PANEL_VALUES.DEFAULT_ERROR_MESSAGE);

    pushToast({
      severity: NotificationSeverity.error,
      message,
    });
  };

  const showSuccessToast = (message: string) => {
    pushToast({
      severity: NotificationSeverity.success,
      message,
    });
  };

  const handleToastForResult = (success: boolean, successLabel: string, errorLabel: string, error?: unknown) => {
    if (success) {
      showSuccessToast(successLabel);
    } else {
      showApiErrorToast(error, errorLabel);
    }
  };

  const run = useCallback(async () => {
    if (!uuid) return;
    setIsProcessing(true);

    const kybResult = await triggerAutoKyb({ uuid });
    const kybSuccess = !('error' in kybResult);

    handleToastForResult(
      kybSuccess,
      t(LABELS.PANEL_VALUES.AUTO_KYB_SUCCESS),
      t(LABELS.PANEL_VALUES.AUTO_KYB_FAILED),
      'error' in kybResult ? kybResult.error : undefined,
    );

    let bankSuccess = false;

    if (kybSuccess) {
      const bankResult = await autoCreateBankAccount({ uuid });
      bankSuccess = !('error' in bankResult);

      handleToastForResult(bankSuccess, t(LABELS.PANEL_VALUES.AUTO_CREATE_BANK_SUCCESS), t(LABELS.PANEL_VALUES.AUTO_CREATE_BANK_FAILED));
    }

    if (kybSuccess && bankSuccess) {
      showSuccessToast(t(LABELS.PANEL_VALUES.AUTO_ONBOARDING_SUCCESS));
    }

    setIsProcessing(false);
  }, [uuid, t]);

  return { run, isProcessing };
};
